/** @file This file contains utilities to work with promises. */
import moment from 'moment';
import { always, EitherAsync, identity, Left, Right } from 'purify-ts';
import { Maybe } from '@codegrade/maybe';
import { coerceToError } from './errors';
/**
 * Convert a function that produces a promise to an `EitherAsync`.
 *
 * @param func The function to produce a promise.
 * @returns An EitherAsync, which will resolve to a `Right` if the promises resolves, and resolves
 * to a `Left` if it rejects. The error value will always be a (sub)type of `Error`.
 */
export function toEitherAsync(func) {
    return EitherAsync(async ({ throwE }) => {
        try {
            return await func();
        }
        catch (error) {
            return throwE(coerceToError(error));
        }
    });
}
/**
 * Convert a promise to an `EitherAsync` that has already been started.
 *
 * @param promise The promise to convert to an either async.
 * @returns An EitherAsync that has already started running.
 */
export function toEitherAsyncEager(promise) {
    return toEitherAsync(() => promise);
}
/**
 * Convert a function that returns a promise to return an EitherAsync.
 *
 * @param func The function to convert.
 * @returns A function that returns an `EitherAsync`, as described in `toEitherAsync`.
 */
export function eitherAsyncify(func) {
    return (...args) => toEitherAsync(() => func(...args));
}
/** A wrapper around `Promise.resolve` that has `Promise` bound to it. */
export const resolve = Promise.resolve.bind(Promise);
/**
 * Sleep for the given amount of time.
 *
 * @param time The amount of time to sleep.
 * @returns A promise that will resolve after `time`.
 */
export function sleep(time) {
    return new Promise(_resolve => {
        setTimeout(() => {
            _resolve(undefined);
        }, time.asMilliseconds());
    });
}
/**
 * Retry a function that returns a promise until the returned promise no longer rejects.
 *
 * @param doTry The function to do a single try.
 * @param opts Retry options.
 * @returns A promise that will resolve to the returned value from `doTry` if it succeeds, or it's
 * last rejected error otherwise.
 */
export async function retry(doTry, opts) {
    const jitterFactor = opts.jitterFactor ?? 0.1;
    const backoffFactor = opts.backoffFactor ?? 1.1;
    const initialDelayMs = opts.initialDelay?.asMilliseconds() ?? 1_000;
    const fixupDelay = opts.fixupDelay ?? identity;
    const shouldRetry = opts.shouldRetry;
    const shouldRetryOk = opts.shouldRetryOk ?? always(false);
    for (let nthTry = 1;; nthTry++) {
        try {
            // eslint-disable-next-line no-await-in-loop
            const result = await doTry();
            if (!shouldRetryOk(nthTry, result)) {
                return result;
            }
        }
        catch (err) {
            if (!shouldRetry(nthTry, err)) {
                throw err;
            }
        }
        const jitter = 1 + jitterFactor * (2 * Math.random() - 1);
        const backoff = backoffFactor ** (nthTry - 1);
        const delay = moment.duration(initialDelayMs * jitter * backoff, 'ms');
        // eslint-disable-next-line no-await-in-loop
        await sleep(moment.duration(fixupDelay(delay, nthTry)));
    }
}
/**
 * Preloads a promise and returns a custom `PromiseLike` object.
 *
 * This allows handling the result of the promise (whether resolved or rejected) without letting
 * rejections crash the application. It ensures that errors are caught but can still be handled
 * explicitly later.
 *
 * @param promise The promise to preload and wrap.
 * @returns A custom `PromiseLike` object that can be awaited or chained with `then`, handling both
 * fulfillment and rejection.
 *
 * @example
 * // Example 1: Handling a resolved promise
 * const myPromise = Promise.resolve('Hello World');
 * const preloaded = preload(myPromise);
 * const result = await preloaded;
 * console.log(result); // Output: "Hello World"
 *
 * @example
 * // Example 2: Handling a rejected promise
 * const failingPromise = Promise.reject(new Error('Something went wrong'));
 * // This can be safely dropped without the app crashing.
 * const preloadedFail = preload(failingPromise);
 * try {
 *   await preloadedFail;
 * } catch (error) {
 *   console.error(error.message); // Output: "Something went wrong"
 * }
 */
export function preload(promise) {
    const wrappedPromise = promise.then(Right, Left);
    return {
        then: async (onfulfilled, onrejected) => {
            const awaited = await wrappedPromise;
            if (awaited.isRight()) {
                const result = awaited.extract();
                if (onfulfilled == null) {
                    // Workaround for TypeScript bug #58619
                    return result;
                }
                return await onfulfilled(result);
            }
            else {
                const error = awaited.extract();
                if (onrejected == null) {
                    throw error;
                }
                return await onrejected(error);
            }
        },
    };
}
/**
 * Executes a promise with a timeout, the result wrapped in a `Maybe`.
 *
 * @param promise The promise to execute.
 * @param timeoutTime The duration after which the timeout occurs.
 * @returns A `Just` containing the resolved value, or a `Nothing` if timed out.
 */
export async function timeout(promise, timeoutTime) {
    return await Promise.race([
        sleep(timeoutTime).then(() => Maybe.empty()),
        promise.then(result => Maybe.of(result)),
    ]);
}
